<template>
	<div>
		<div class="flex xs-12 xs-right">
			<!-- Headline -->
			<div
				class="flex xs-12 xs-between xs-middle mb1"
				v-if="variant === 'headline'"
			>
				<div class="lh125 fw7 c1 fs5">{{ headline || $t("stakeholders") }}</div>
				<j-button type="tertiary" @click="$modal.show(instanceKey)">
					<crm-icon name="add" size="small" color="c2" />
					{{ $t("add") }}
				</j-button>
			</div>
		</div>

		<!-- List fetched stakeholders -->
		<j-card
			v-for="(item, i) in stakeholders"
			:key="i"
			class="flex xs-12 xs-between bg4--l3 p4 mb2 xs-middle"
		>
			<div class="fs3">
				<div class="mb1">
					<router-link
						v-if="item.entity"
						:to="{ name: 'InternalsDetail', params: { id: item.entity.id } }"
					>{{ item.entity.name }}</router-link>
					<span v-else>---</span>
				</div>
				<div>{{ getType(item.type) }}</div>
			</div>
			<crm-icon
				class="cu2"
				name="delete"
				color="cError"
				@click.native="removeStakeholder(item.id)"
			/>
		</j-card>

		<!-- No data -->
		<j-card
			v-if="!stakeholders || stakeholders.length === 0"
			class="bg4--l3 p4 ta2 fs3 c6 mb2"
		>{{ $t("errors.no_data") }}</j-card>

		<!-- Add button for variant no-headline -->
		<div class="xs-12 flex xs-right" v-if="variant === 'no-headline'">
			<j-button
				type="primary"
				size="xsmall"
				@click="$modal.show(instanceKey)"
			>{{ $t("add") }}</j-button>
		</div>

		<!-- Add stakeholder modal -->
		<core-modal :name="instanceKey" :headline="$t('add_stakeholder')">
			<stakeholders-add
				:caseId="caseId"
				:proposalId="proposalId"
				:offerId="offerId"
				:policyId="policyId"
				:claimId="claimId"
				:transactionId="transactionId"
				:stakeholdersOf="stakeholdersOf"
				:typeOptions="types"
				@stakeholder:added="handleStakeholderAdded($event)"
			/>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard/"
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import CoreModal from "@/modules/core/components/CoreModal"
import StakeholdersAdd from "./StakeholdersAdd.vue"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

export default {
	components: {
		JCard,
		CrmIcon,
		JButton,
		CoreModal,
		StakeholdersAdd
	},

	props: {
		// Data that we get from API call in parent
		stakeholders: {
			type: Array,
			default: () => []
		},

		// List of types
		types: {
			type: Array,
			default: () => []
		},

		// When variant is `headline`, we display headline and add button next to it
		// When variant is `no-headline`, we don't display headline and add button is below the list
		variant: {
			validator(val) {
				return ["no-headline", "headline"].includes(val)
			},
			default: "headline"
		},

		// Possible ids for mutations variables
		// Based on stakeholdersOf you have to pass proper props
		caseId: String,
		proposalId: String,
		offerId: String,
		policyId: String,
		claimId: String,
		transactionId: String,

		// For what stakeholder we are doing mutations
		stakeholdersOf: {
			validator(val) {
				return [
					"case",
					"proposal",
					"offer",
					"policy",
					"transaction",
					"claim"
				].includes(val)
			}
		},

		headline: {
			type: String
		}
	},

	data() {
		return {
			// We use this for modal window
			instanceKey: Math.random().toString()
		}
	},

	methods: {
		// Based on stakeholdersOf, decide mutation
		async removeStakeholder(stakeholderId) {
			if (this.stakeholdersOf === "case")
				this.removeStakeholderFromCase(stakeholderId)
			if (this.stakeholdersOf === "proposal")
				this.removeStakeholderFromProposal(stakeholderId)
			if (this.stakeholdersOf === "offer")
				this.removeStakeholderFromProposalOffer(stakeholderId)
			if (this.stakeholdersOf === "policy")
				this.removeStakeholderFromPolicy(stakeholderId)
			if (this.stakeholdersOf === "transaction")
				this.removeStakeholderFromTransaction(stakeholderId)
			if (this.stakeholdersOf === "claim")
				this.removeStakeholderFromClaim(stakeholderId)
		},

		async removeStakeholderFromCase(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromCase(
					$caseId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromCase(
						caseId: $caseId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromCase",
				stakeholderId
			})
		},

		async removeStakeholderFromProposal(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromProposal(
					$caseId: String!
					$proposalId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromProposal(
						caseId: $caseId
						proposalId: $proposalId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromProposal",
				stakeholderId
			})
		},

		async removeStakeholderFromProposalOffer(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromProposalOffer",
				stakeholderId
			})
		},

		async removeStakeholderFromPolicy(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromPolicy(
					$policyId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromPolicy(
						policyId: $policyId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				policyId: this.policyId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromPolicy",
				stakeholderId
			})
		},

		async removeStakeholderFromPolicy(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromPolicy(
					$policyId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromPolicy(
						policyId: $policyId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				policyId: this.policyId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromPolicy",
				stakeholderId
			})
		},

		async removeStakeholderFromTransaction(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromTransaction(
					$transactionId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromTransaction(
						transactionId: $transactionId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				transactionId: this.transactionId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromTransaction",
				stakeholderId
			})
		},

		async removeStakeholderFromClaim(stakeholderId) {
			const query = gql`
				mutation removeStakeholderFromClaim(
					$claimId: String!
					$stakeholderId: String!
				) {
					removeStakeholderFromClaim(
						claimId: $claimId
						stakeholderId: $stakeholderId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				claimId: this.claimId,
				stakeholderId
			}

			this.executeMutation({
				query,
				variables,
				mutationName: "removeStakeholderFromClaim",
				stakeholderId
			})
		},

		/**
		 * Helper function to execute mutation call
		 * It handles errors displaying as well
		 */
		async executeMutation({
			query = "",
			variables = {},
			mutationName = "",
			stakeholderId
		}) {
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			// Handle errors
			if (res?.data?.[mutationName]?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.[mutationName]?.errors?.[0] || this.$t("errors.general")
				)
				return Promise.resolve()
			}

			// Success
			this.$emit("stakeholder:removed", stakeholderId)
			return Promise.resolve()
		},

		getType(type) {
			return this.types?.length > 0
				? this.types?.find(item => item.value === type)?.name
				: type
		},

		handleStakeholderAdded(value) {
			this.$modal.hide(this.instanceKey)
			this.$emit("stakeholder:added", value)
		}
	}
}
</script>
